import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, Clock, Facebook, Linkedin, Twitter } from "lucide-react";
import { COMPANY } from "./data";

const ABOUT_LINKS = [
  { label: "Who We Are", to: "/about" },
  { label: "Our Vision", to: "/vision" },
  { label: "Our Team", to: "/team" },
  { label: "Our Foot Map", to: "/foot-map" },
];

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header data-testid="site-navbar" className="fixed top-0 inset-x-0 z-50">
      <div className="hidden sm:block bg-slate-950 border-b border-slate-800/60" data-testid="nav-topbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-6">
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5" /> {COMPANY.phone}
            </a>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Sat - Thu: 9AM - 6PM
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-white transition-colors" data-testid="topbar-facebook">
              <Facebook className="w-3.5 h-3.5" />
            </a>
            <span aria-label="LinkedIn" className="hover:text-white transition-colors cursor-pointer"><Linkedin className="w-3.5 h-3.5" /></span>
            <span aria-label="Twitter" className="hover:text-white transition-colors cursor-pointer"><Twitter className="w-3.5 h-3.5" /></span>
          </div>
        </div>
      </div>

      <div className="bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Laneeka International logo" className="h-11 w-11 object-contain rounded bg-white p-0.5" />
            <span className="leading-tight">
              <span className="block font-bold tracking-tight text-white text-lg">Laneeka <span className="text-blue-400">International</span></span>
              <span className="block text-[10px] uppercase tracking-widest text-slate-500">Partner in Industrial Belting</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
            <NavLink to="/" end data-testid="nav-link-home" className={linkCls}>Home</NavLink>
            <div className="relative group">
              <button data-testid="nav-link-about" className="flex items-center gap-1 text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
                About <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <div className="bg-slate-900 border border-slate-800 rounded-lg shadow-xl py-2 w-44">
                  {ABOUT_LINKS.map((l) => (
                    <NavLink key={l.to} to={l.to} data-testid={`nav-dropdown-${l.to.slice(1)}`}
                      className="block px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
            <NavLink to="/products" data-testid="nav-link-products" className={linkCls}>Products &amp; Services</NavLink>
            <NavLink to="/news" data-testid="nav-link-news" className={linkCls}>News</NavLink>
            <NavLink to="/career" data-testid="nav-link-career" className={linkCls}>Career</NavLink>
            <NavLink to="/contact" data-testid="nav-link-contact" className={linkCls}>Contact</NavLink>
          </nav>

          <div className="hidden lg:block">
            <button data-testid="nav-quote-cta" onClick={() => navigate("/contact")}
              className="bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-5 py-3 transition-colors">
              Request a Quote
            </button>
          </div>

          <button data-testid="nav-mobile-toggle" className="lg:hidden p-2 text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <nav data-testid="nav-mobile-menu" className="lg:hidden border-t border-slate-800 bg-slate-950 max-h-[70vh] overflow-y-auto">
            <div className="px-6 py-3 flex flex-col">
              <NavLink to="/" end data-testid="nav-mobile-link-home" onClick={() => setOpen(false)} className="py-3 text-sm font-medium text-slate-200 border-b border-slate-800">Home</NavLink>
              {ABOUT_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`nav-mobile-link-${l.to.slice(1)}`} onClick={() => setOpen(false)}
                  className="py-3 text-sm font-medium text-slate-200 border-b border-slate-800">
                  {l.label}
                </NavLink>
              ))}
              <NavLink to="/products" data-testid="nav-mobile-link-products" onClick={() => setOpen(false)} className="py-3 text-sm font-medium text-slate-200 border-b border-slate-800">Products &amp; Services</NavLink>
              <NavLink to="/news" data-testid="nav-mobile-link-news" onClick={() => setOpen(false)} className="py-3 text-sm font-medium text-slate-200 border-b border-slate-800">News</NavLink>
              <NavLink to="/career" data-testid="nav-mobile-link-career" onClick={() => setOpen(false)} className="py-3 text-sm font-medium text-slate-200 border-b border-slate-800">Career</NavLink>
              <NavLink to="/contact" data-testid="nav-mobile-link-contact" onClick={() => setOpen(false)} className="py-3 text-sm font-medium text-slate-200 border-b border-slate-800">Contact</NavLink>
              <button data-testid="nav-mobile-quote-cta" onClick={() => { setOpen(false); navigate("/contact"); }}
                className="my-3 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider py-3">
                Request a Quote
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
